import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Play } from "lucide-react";
import { API } from "@/lib/api";
import { ChannelGrid, useChannels } from "@/components/ChannelGrid";
import { MediaCard } from "@/components/MediaCard";
import { LiveBadge } from "@/components/Equalizer";
import { usePlayer } from "@/context/PlayerContext";

const HERO = "https://images.unsplash.com/photo-1620577610365-86c411bad78d?w=2000&q=80";

const steps = [
  ["01", "Arma tu Press Kit", "Bio, fotos, redes, letra y tu canción en una página pública para compartir."],
  ["02", "Postula a la radio", "Gratis. Un curador escucha y decide si tu canción entra en el canal."],
  ["03", "Envía a medios", "Radios, podcasts y blogs reales. Pagas con créditos y te responden en 7 días o te los devolvemos."],
  ["04", "Destácate", "Por 3 créditos tu canción suena más seguido durante 7 días con la etiqueta Destacada."],
];

function Hero() {
  const channels = useChannels();
  const { playChannel } = usePlayer();
  return (
    <section className="relative flex min-h-[88vh] items-end overflow-hidden">
      <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0E] via-[#0A0A0E]/70 to-[#0A0A0E]/20" />
      <div className="relative mx-auto w-full max-w-[1400px] px-4 pb-20 md:px-8">
        <div className="fade-up"><LiveBadge label="EN VIVO · 7 CANALES 24/7" /></div>
        <h1 className="fade-up mt-6 max-w-4xl font-heading text-4xl font-extrabold leading-[0.95] tracking-tight text-white sm:text-5xl lg:text-7xl" style={{ animationDelay: "80ms" }}>
          La radio curada de la música <span className="text-[#FF4500]">independiente</span>.
        </h1>
        <p className="fade-up mt-6 max-w-xl text-base text-slate-300 md:text-lg" style={{ animationDelay: "160ms" }}>
          Canales temáticos armados por curadores, artistas que suenan de verdad y un puente directo a radios, podcasts y blogs de Iberoamérica.
        </p>
        <div className="fade-up mt-10 flex flex-wrap gap-4" style={{ animationDelay: "240ms" }}>
          <button data-testid="hero-listen-btn" onClick={() => channels?.[0] && playChannel(channels[0])} className="flex items-center gap-2 rounded-full bg-[#FF4500] px-7 py-3.5 font-semibold text-white transition-transform hover:scale-[1.03]">
            <Play className="h-4 w-4 fill-white" /> Escuchar la radio
          </button>
          <Link to="/sign-up" data-testid="hero-artist-btn" className="flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-7 py-3.5 font-semibold text-white backdrop-blur transition-colors hover:bg-white/10">
            Enviar mi canción <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function MediaTeaser() {
  const [media, setMedia] = useState([]);
  useEffect(() => { axios.get(`${API}/media`).then((r) => setMedia(r.data)).catch(() => {}); }, []);
  return (
    <section className="mx-auto max-w-[1400px] px-4 py-24 md:px-8">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="font-mono text-xs tracking-widest text-amber-500/90">DIRECTORIO DE MEDIOS</p>
          <h2 className="mt-3 font-heading text-3xl font-bold text-white sm:text-4xl">{media.length} medios esperan tu canción</h2>
        </div>
        <Link to="/medios" data-testid="home-media-link" className="flex items-center gap-2 text-sm font-semibold text-[#FF7A45] hover:text-white">Ver todos los medios <ArrowRight className="h-4 w-4" /></Link>
      </div>
      <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {media.slice(0, 4).map((m) => <MediaCard key={m.id} m={m} />)}
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <section id="canales" className="mx-auto max-w-[1400px] px-4 py-24 md:px-8">
        <p className="font-mono text-xs tracking-widest text-amber-500/90">CANALES 24/7</p>
        <h2 className="mt-3 max-w-2xl font-heading text-3xl font-bold text-white sm:text-4xl">Elige un canal y deja que suene mientras navegas.</h2>
        <div className="mt-10"><ChannelGrid /></div>
      </section>
      <section className="border-y border-white/5 bg-[#12121A]">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-4 py-24 md:px-8 lg:grid-cols-[1fr_2fr]">
          <div>
            <p className="font-mono text-xs tracking-widest text-amber-500/90">PARA ARTISTAS</p>
            <h2 className="mt-3 font-heading text-3xl font-bold text-white sm:text-4xl">De tu sala de ensayo al aire.</h2>
            <p className="mt-4 text-slate-400">1 crédito = USD 2. Cada envío cuesta entre 1 y 3 créditos y el medio recibe el 70%.</p>
            <Link to="/creditos" data-testid="home-credits-link" className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-black transition-colors hover:bg-slate-200">Ver paquetes de créditos</Link>
          </div>
          <div className="grid gap-px overflow-hidden rounded-xl border border-white/10 bg-white/10 sm:grid-cols-2">
            {steps.map(([n, t, d]) => (
              <div key={n} className="bg-[#12121A] p-8">
                <span className="font-mono text-sm text-[#FF4500]">{n}</span>
                <h3 className="mt-4 font-heading text-xl font-bold text-white">{t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <MediaTeaser />
    </div>
  );
}
